import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserRoundCog, Loader2, CheckCircle, AlertTriangle, Sparkles } from 'lucide-react';
import useCodeName from './useCodeName';
import { randomCodeName } from '../../utils/randomCodeName';

/**
 * CodeNameCard — Settings tile that lets a member view and rename their
 * privacy Code Name. This is the single most important building block of
 * the Privacy Uplift; everything downstream (header display, PDF headers,
 * admin dashboards) reads this value.
 *
 * Format rules (mirrored in backend `/utils/code_name.py`):
 *   Two words, letters only, ALL CAPS, 4–16 characters total.
 */
const CodeNameCard = () => {
  const {
    currentName, draft, availability, checking, saving, error,
    onDraftChange, save, applySuggestion, isDirty,
  } = useCodeName();

  const showTick = !checking && availability?.available === true;
  const showCross = !checking && availability && availability.available === false;
  const canSave = isDirty && !checking && !saving && (availability?.available === true);

  return (
    <Card data-testid="code-name-card" className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserRoundCog className="w-5 h-5 text-emerald-600" aria-hidden="true" />
          <span>Your Code Name</span>
          {currentName && (
            <Badge
              data-testid="code-name-current-badge"
              className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            >
              {currentName}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          A private two-word name used in place of your real name across MyInvestFolio.
          Your real name stays encrypted and untouched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <label className="text-sm font-medium text-slate-700 block">
            Try a new one
          </label>
          <button
            type="button"
            data-testid="code-name-surprise-me"
            onClick={() => onDraftChange(randomCodeName())}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100 transition-colors"
          >
            <Sparkles className="w-3 h-3" aria-hidden="true" />
            Surprise me
          </button>
        </div>
        <div className="relative">
          <Input
            data-testid="code-name-input"
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="e.g. AMBER FALCON"
            maxLength={16}
            spellCheck={false}
            autoComplete="off"
            className="uppercase tracking-wide pr-10"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {checking && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-label="Checking availability" />}
            {showTick && <CheckCircle className="w-4 h-4 text-emerald-600" aria-label="Available" />}
            {showCross && <AlertTriangle className="w-4 h-4 text-amber-600" aria-label="Not available" />}
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Two words · letters only · 4 to 16 characters total. We&apos;ll always show it in capitals.
        </p>
        {isDirty && showTick && (
          <p
            data-testid="code-name-live-preview"
            className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2"
          >
            You&apos;ll appear as <span className="font-semibold tracking-wide">{draft.trim()}</span> across the app.
          </p>
        )}

        {availability && !availability.available && (
          <div data-testid="code-name-suggestions" className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs font-medium text-slate-700 mb-2">
              {availability.reason === 'taken'
                ? 'That Code Name is already in use. Try one of these:'
                : 'That doesn\'t look like a valid Code Name. Try one of these:'}
            </p>
            <div className="flex flex-wrap gap-2">
              {(availability.suggestions || []).map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`code-name-suggestion-${s.replace(/\s+/g, '-')}`}
                  onClick={() => applySuggestion(s)}
                  className="px-2.5 py-1 rounded-full bg-white border border-slate-300 text-xs font-medium text-slate-700 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription data-testid="code-name-error">{error}</AlertDescription>
          </Alert>
        )}

        <Button
          data-testid="code-name-save-btn"
          onClick={save}
          disabled={!canSave}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : 'Save Code Name'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CodeNameCard;
